import { VelocityEnv } from '@velocity-exchange/sdk';
import { useCallback } from 'react';
import { notify } from 'src/utils/notifications';
import { sleep } from 'src/utils/dateTime';
import { getRpcOptions } from 'src/utils/rpc';
import { CUSTOM_RPC_LABEL } from 'src/constants/constants';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useModal from 'src/components/Modals/hooks/useModal';
import { GLOBAL_SETTINGS_CLIENT } from 'src/providers/settingsProvider';
import { useRunConnectionSteps } from './useRunConnectionSteps';
import { useHandleSubscriptionSuccess } from './useHandleSubscriptionSuccess';
import { RpcEndpoint } from '@velocity-exchange/common';
import Env, {
	SubscriberType,
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { useUnsubscribeClients } from './useUnsubscribeClients';
import Button from 'src/components/primitives/Button/Button';

/**
 * Initialize or update the app's connection, clearing house, user account, etc. and subscribes to all.
 */
export const useUpdateConnection = () => {
	const appEventEmitter = useVelocityStore((s) => s.appEventEmitter);

	const setVelocityStore = useVelocityStore((s) => s.set);
	const { show: showModal } = useModal();
	const { runConnectionSteps } = useRunConnectionSteps();
	const { handleSubscriptionSuccess } = useHandleSubscriptionSuccess();
	const { unsubscribeClients } = useUnsubscribeClients();

	const updateConnection = useCallback(
		async (
			newRpc: RpcEndpoint,
			subscriberType: SubscriberType,
			env: VelocityEnv,
			context: 'initializing' | 'changeConnection' | 'recoveryAttempt'
		) => {
			setVelocityStore((s) => {
				s.rpcUrl = newRpc.value;
			});

			if (context === 'changeConnection') {
				notify({
					type: 'info',
					message: 'Switching to new RPC.',
				});
			}

			let connectedSuccessfully = false;
			const initialResult = await runConnectionSteps(
				newRpc,
				subscriberType,
				env
			);

			// A newer attempt owns the connection now; retrying or notifying here
			// would fight it.
			if (initialResult === 'superseded') return;

			const subscriptionResult = initialResult === 'success';
			connectedSuccessfully = subscriptionResult;

			const handleOpenRpcSettings = () => {
				showModal({ type: 'settings', props: { view: 'network' } });
			};

			/**
			 * To handle connection failures what do we want to do?
			 * - First: Delay then try again
			 * - Second:
			 * 	- If they're using a custom RPC then notify them that we're having trouble with the custom RPC and exit
			 * 	- If using a standard RPC, then try another available one
			 * 	- Third: Things aren't working .. Notify the user
			 * 	- Is there an alternative status we can display to communicate that connection didn't work, rathe than just showing the "Loading" state which could be confusing. A retry button too perhaps?
			 * 	- Might be good to provide options to fix the state like: Reset Local Storage
			 */

			const toastId = 'RPC_CONNECTION_TOAST';
			const initialConnectionFailed = !subscriptionResult;

			if (!subscriptionResult) {
				let retryLoop = true;
				let retryCount = 0;
				while (retryLoop) {
					const settings = syncGetCurrentSettings();
					const usingCustomRpc =
						settings?.rpcEndpoint?.label === CUSTOM_RPC_LABEL;

					switch (retryCount) {
						case 0: {
							console.log(`CONNECTION RETRY 0 : Retrying ${newRpc.value}`);
							notify({
								type: 'info',
								message: 'Connecting to Velocity.',
								id: toastId,
							});
							// Retry after a delay
							await sleep(3000);
							const retryAttemptResult = await runConnectionSteps(
								newRpc,
								subscriberType,
								env
							);
							if (retryAttemptResult === 'superseded') return;
							if (retryAttemptResult === 'success') {
								retryLoop = false;
								connectedSuccessfully = true;
							}
							break;
						}
						default: {
							const rpcOffset = retryCount - 1;

							const sdkEnv = Env.sdkEnv;
							const availableRpcs = getRpcOptions(sdkEnv);

							const exhaustedAllOptions = rpcOffset >= availableRpcs.length;

							if (exhaustedAllOptions) {
								// # Handle unrecoverale RPC issues
								console.log(`CONNECTION FAILED`);
								unsubscribeClients();
								// TODO: We could potentially handle this more gracefully but it's a bigger job than I want to take right now. Think we should offer the user a modal with some options.
								notify({
									type: 'error',
									message: 'RPC Connection Error.',
									description:
										'Unable to connect to Velocity. Please try refreshing the page or changing the RPC connection settings.',
									id: toastId,
									showUntilCancelled: true,
									action: {
										type: 'custom',
										content: (
											<Button
												size="sm"
												variant="stroke"
												onClick={handleOpenRpcSettings}
											>
												Change RPC
											</Button>
										),
									},
								});
								retryLoop = false;
								break;
							} else {
								// # Handle retry with a new RPC
								const nextRpcToTry = availableRpcs[rpcOffset];
								console.log(
									`CONNECTION RETRY ${retryCount} : Retrying ${nextRpcToTry.value}`
								);

								await sleep(1000);

								const nextRpcResult = await runConnectionSteps(
									nextRpcToTry,
									subscriberType,
									sdkEnv
								);

								if (nextRpcResult === 'superseded') return;

								const retryAttemptResultSuccess = nextRpcResult === 'success';

								if (usingCustomRpc && retryAttemptResultSuccess) {
									notify({
										type: 'warning',
										message: 'Custom RPC Error.',
										description:
											'There was an issue connecting to Velocity using your custom RPC configuration, switched to default RPC config.',
										showUntilCancelled: true,
										id: toastId,
									});
								}

								if (retryAttemptResultSuccess) {
									retryLoop = false;
									GLOBAL_SETTINGS_CLIENT.updateSettings({
										rpcEndpoint: nextRpcToTry,
									});
									connectedSuccessfully = true;
								}
							}
							break;
						}
					}
					retryCount++;
				}
			}

			if (connectedSuccessfully) {
				if (initialConnectionFailed) {
					notify({
						type: 'success',
						message: 'Connected to Velocity.',
						id: toastId,
					});
				}
				appEventEmitter.emit('rpcAndClientSubscribed');
			}
		},
		[
			appEventEmitter,
			runConnectionSteps,
			handleSubscriptionSuccess,
			showModal,
			unsubscribeClients,
		]
	);

	return { updateConnection };
};
